import React, {useState} from "react";
import {useForm, SubmitHandler, FormProvider} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import {useAuth} from "hooks/useAuth";
import {useServerErrors} from "hooks/useServerErrors";
import FormField from "shared/form/FormField";
import ErrorsList from "shared/ui/ErrorsList";
import {APIPostRegister} from "../api/login";

interface IFormRegister {
    last_name: string;
    first_name: string;
    email: string;
    password?: string;
    confirm_password?: string;
}

const schema = yup
    .object({})
    .required();

const RegisterForm = () => {
    const {register, update} = useAuth();
    const [errorMessage, setErrorMessage] = useState<string>("");

    const methods = useForm<IFormRegister>({
        resolver: yupResolver(schema),
    });

    const { handleServerErrors } = useServerErrors<IFormRegister>(methods.setError);

    const handleUserRegister = (apiResponse: any) => {
        const authData = apiResponse.data.data.register;
        register({
            firstname: authData.first_name || "",
            lastname: authData.last_name || "",
            email: authData.email || "",
            position: authData.position || "",
            profilePhoto: authData.profile_photo || "",
            token: `Bearer ${authData.token}`,
            menu: authData.menu || [],
            email_verified: authData.email_verified,
        });

        const { label, value, ...tenant } = apiResponse.data.data.domain;
        update("tenant", tenant);
    };

    const handleRegisterError = (err: any) => {
        if (err?.response?.status === 400) {
            const errors = err.response.data.data;
            handleServerErrors(errors);
        } else {
            update("error", {show: true, code: "A001"});
        }
    }

    const onSubmit: SubmitHandler<IFormRegister> = (data) => {
        update("loading", {show: true, modal: true});
        APIPostRegister({
            domain: "teachers",
            first_name: data.first_name,
            last_name: data.last_name,
            email: data.email,
            password: data.password,
            confirm_password: data.confirm_password,
        })
            .then((response) => {
                if (response.data.success && response.data.data?.register.token !== null) {
                    // console.log("postRegister OK", response);
                    setErrorMessage("");
                    handleUserRegister(response);
                } else {
                    update("error", {show: true, code: "A002"});
                }
            })
            .catch(handleRegisterError)
            .finally(() => update("loading", {show: false}));
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
                <Box py={1}>
                    <FormField
                        name="first_name"
                        type="text"
                        defaultValue=""
                        label="Prenume"
                        autoFocus={true}
                        error={!!methods.formState.errors.first_name?.message}
                        errorMessage={methods.formState.errors.first_name?.message}
                    />
                </Box>
                <Box py={1}>
                    <FormField
                        name="last_name"
                        type="text"
                        defaultValue=""
                        label="Nume"
                        error={!!methods.formState.errors.last_name?.message}
                        errorMessage={methods.formState.errors.last_name?.message}
                    />
                </Box>
                <Box py={1}>
                    <FormField
                        name="email"
                        type="text"
                        defaultValue=""
                        label="Adresa de e-mail"
                        autoComplete="email"
                        error={!!methods.formState.errors.email?.message}
                        errorMessage={methods.formState.errors.email?.message}
                    />
                </Box>
                <Box py={1}>
                    <FormField
                        name="password"
                        type="password"
                        defaultValue=""
                        label="Parola"
                        autoComplete="password"
                        error={!!methods.formState.errors.password?.message}
                        errorMessage={methods.formState.errors.password?.message}
                    />
                </Box>
                <Box py={1}>
                    <FormField
                        name="confirm_password"
                        type="password"
                        label="Confirmă parola"
                        error={!!methods.formState.errors.confirm_password?.message}
                        errorMessage={methods.formState.errors.confirm_password?.message}
                    />
                </Box>
                <Box sx={{mt: 1, mb: 3}}>
                    <Button type="submit" variant="contained">Înregistrare</Button>
                    <ErrorsList show={errorMessage !== ""}>{errorMessage}</ErrorsList>
                </Box>
            </form>
        </FormProvider>
    );
};

export default RegisterForm;
